// BL598 Phase 2 — `gridctl describe grid <slug>`.
//
// Shows a grid's details: display name, your role, member count, space count,
// tier (deployment mode), and timestamps. Resolves the slug to its grid_id via
// GET /api/v2/grids, then reads GET /api/v2/grids/:grid_id for the detail shape.
//
// BL621 Phase 3 (Decision 042 §"Deletes") — `describe grid` absorbs the legacy
// `org status` verb: it also reads GET /api/v2/grids/:slug/status and appends the
// wildcard-TLS provisioning state (DNS / cert / ready). The status lookup is
// best-effort — a grid with no provisioning record (or an older API) still
// renders the core detail block.

import { Command } from 'commander';
import type { IV2Client } from '../../api/v2-client.js';
import { getV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';
import { CloudGridError } from '../../utils.js';

// Decision 045: dual-emit — prefer `grid_id`, fall back to legacy `org_id`.
interface OrgListRow {
  grid_id?: string;
  org_id?: string;
  slug: string;
  name: string;
  role?: string;
}

interface OrgDetail {
  grid_id?: string;
  org_id?: string;
  slug: string;
  name: string;
  description: string | null;
  org_type: string;
  deployment_mode: string;
  member_count: number;
  space_count: number;
  created_at: string;
  updated_at: string;
}

// ── Provisioning state (absorbed from the legacy `org status`, backlog 255) ──

interface OrgStatusResponse {
  slug: string;
  dns: { exists: boolean; record_id: string | null };
  certificate: {
    exists: boolean;
    ready: boolean;
    reason?: string;
    message?: string;
  };
  ready: boolean;
}

const TICK = '✓';
const CROSS = '✗';

/**
 * Read the grid's wildcard-TLS provisioning state. Best-effort: a 404 (no
 * provisioning record) or any other failure resolves to `null` so the core
 * describe-grid block always renders.
 */
async function fetchProvisioning(
  client: IV2Client,
  slug: string,
): Promise<OrgStatusResponse | null> {
  try {
    return await client.get<OrgStatusResponse>(
      `/api/v2/grids/${encodeURIComponent(slug)}/status`,
    );
  } catch {
    return null;
  }
}

function formatProvisioning(status: OrgStatusResponse): string {
  const dnsLine = status.dns.exists
    ? `  DNS:         *.${status.slug}.cloudgrid.io  ${TICK} resolved`
    : `  DNS:         *.${status.slug}.cloudgrid.io  ${CROSS} not yet provisioned`;

  let certLine: string;
  if (!status.certificate.exists) {
    certLine = `  Cert:        wildcard-${status.slug}-cloudgrid  ${CROSS} not yet created`;
  } else if (status.certificate.ready) {
    certLine = `  Cert:        wildcard-${status.slug}-cloudgrid  ${TICK} ready`;
  } else {
    const detail = status.certificate.reason
      ? ` (${status.certificate.reason}${status.certificate.message ? `: ${status.certificate.message}` : ''})`
      : '';
    certLine = `  Cert:        wildcard-${status.slug}-cloudgrid  issuing${detail}`;
  }

  const readyLine = status.ready
    ? `  Provisioned: ${TICK} ready`
    : `  Provisioned: waiting on ${status.certificate.ready ? 'DNS' : 'cert'}`;

  return [dnsLine, certLine, readyLine].join('\n');
}

export interface DescribeGridOptions {
  json?: boolean;
  output?: string;
}

export async function runDescribeGrid(
  client: IV2Client,
  slug: string,
  opts: DescribeGridOptions,
): Promise<void> {
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });

  // Resolve slug → grid_id. The detail route is keyed by grid_id; /grids is the
  // membership-scoped list that maps slug to id (and carries the caller role).
  const list = await client.get<{ orgs: OrgListRow[] }>('/api/v2/grids');
  const match = (list.orgs ?? []).find((o) => o.slug === slug);
  if (!match) {
    const available = (list.orgs ?? []).map((o) => o.slug).join(', ') || '(none)';
    throw new CloudGridError(
      `You are not a member of grid '${slug}'. Available: ${available}`,
    );
  }
  const gridId = match.grid_id ?? match.org_id;
  if (!gridId) {
    throw new CloudGridError(`Grid '${slug}' is missing an id in the server response.`);
  }

  const detail = await client.get<OrgDetail>(
    `/api/v2/grids/${encodeURIComponent(gridId)}`,
  );

  const provisioning = await fetchProvisioning(client, slug);

  out.json(() => ({
    ...detail,
    role: match.role ?? null,
    provisioning: provisioning ?? null,
  }));
  out.human(() => {
    const lines: string[] = [];
    lines.push(`${detail.name} (${detail.slug})`);
    if (detail.description) lines.push(`  ${detail.description}`);
    if (match.role) lines.push(`  Your role:   ${match.role}`);
    lines.push(`  Type:        ${detail.org_type}`);
    lines.push(`  Tier:        ${detail.deployment_mode}`);
    lines.push(`  Members:     ${detail.member_count}`);
    lines.push(`  Spaces:      ${detail.space_count}`);
    lines.push(`  Created:     ${detail.created_at}`);
    if (provisioning) {
      lines.push('');
      lines.push(formatProvisioning(provisioning));
    }
    return lines.join('\n');
  });
}

export const describeGridCommand = addOutputOption(
  new Command('grid')
    .description('Show a grid: role, members, spaces, tier, wildcard-TLS state')
    .argument('<slug>', 'Grid slug')
    .option('--json', 'machine-readable output (alias of -o json)'),
).action(async (slug: string, opts: DescribeGridOptions) => {
  try {
    const client = await getV2Client();
    await runDescribeGrid(client, slug, opts);
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
